// Provenance + safety framing for alternate-part results.
//
// Shared by the RFQ-detail panel and the standalone /alt-parts page so the two
// can never drift into describing the same data differently.
//
// Why this exists: the resolver returns part numbers catalogued against the
// same National Stock Number. That is federal-catalog cross-reference evidence
// — it does NOT mean the parts are interchangeable, approved, or installable in
// place of one another. Rendering them as bare "alternates" invites a purchase
// decision the data cannot support. Deliberately neutral styling: no green
// check, no red cross, nothing that reads as a go/no-go on installation.

import type { AltPartsResponse } from '@/lib/api';

/** Snapshots older than this are called out. PUB LOG publishes monthly, so ~2
 *  months without a refresh means at least one release has been missed. */
const STALE_AFTER_DAYS = 60;

function formatVintage(dataset: AltPartsResponse['dataset']): string | null {
  if (!dataset) return null;
  const parts: string[] = [];
  if (dataset.pubDate) parts.push(`${dataset.pubDate} edition`);
  if (dataset.loadedAt) {
    const d = new Date(dataset.loadedAt);
    if (!Number.isNaN(d.getTime())) {
      parts.push(
        `imported ${d.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })}`,
      );
    }
  }
  return parts.length ? parts.join(', ') : null;
}

function isStale(dataset: AltPartsResponse['dataset']): boolean {
  if (!dataset?.loadedAt) return false;
  const d = new Date(dataset.loadedAt);
  if (Number.isNaN(d.getTime())) return false;
  return Date.now() - d.getTime() > STALE_AFTER_DAYS * 86_400_000;
}

/** The relationship label — what kind of match this is. */
export function AltPartRelationshipLabel() {
  return (
    <span className="pill pill-info" title="Catalogued against the same National Stock Number">
      Same-NSN reference · DLA FLIS
    </span>
  );
}

/** The caveat that must accompany any rendering of these results. */
export function AltPartCaveat({ className = '' }: { className?: string }) {
  return (
    <p className={`text-[11px] leading-relaxed text-foreground-muted ${className}`}>
      These part numbers share a National Stock Number in the federal catalog.
      That is a <span className="font-medium text-foreground">cross-reference, not an approved alternate</span> —
      it is not an FAA approval, an OEM supersession, or a statement of
      interchangeability. Verify applicability, configuration and installation
      approval against applicable approved data before purchasing or quoting.
    </p>
  );
}

/** Source attribution plus the vintage of the loaded snapshot. */
export function AltPartSourceLine({
  dataset,
  className = '',
}: {
  dataset: AltPartsResponse['dataset'];
  className?: string;
}) {
  const vintage = formatVintage(dataset);
  const stale = isStale(dataset);
  return (
    <div className={`text-[10px] text-foreground-muted ${className}`}>
      Source: DLA FLIS / PUB LOG (public data)
      {vintage ? ` · ${vintage}` : ''}
      {stale ? ' · snapshot may be out of date' : ''}
    </div>
  );
}
